'use client'

import { useEffect, useMemo, useState } from 'react'

import { ArrowTurnBackwardIcon } from '@hugeicons-pro/core-solid-rounded'
import _ from 'lodash'
import { LoaderCircle } from 'lucide-react'

import { HugeiconsIcon } from '@hugeicons/react'

import { LevelFooter } from '../Footer'
import { HUD } from '../HUD'
import { LevelToolbar } from '../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { SlideStep, useFlow } from '@/shared/lib/steps-flow'
import { showErrorAlert } from '@/shared/lib/ui'
import { Button } from '@/shared/ui/Button'



interface GameSlideProps {
  step: SlideStep
  challengeId: number
}

export function GameSlide({ step, challengeId }: GameSlideProps) {
  const { goPrev, goNext, enableContinue, verifyChallenge } = useFlow()
  const { level, isSubmitting, onChallengeAnswer } = useLevelContext()

  const challenge = level?.active_challenges.find(c => c.id === challengeId)
  const { meta } = challenge ?? {}
  const minimumSeconds = challenge?.minimum_time_to_advance_in_seconds ?? 0
  const slides =
    challenge?.challenge_options?.length
      ? challenge.challenge_options.map(opt => ({
          kind: opt.meta?.kind,
          image: opt.meta?.image,
          iframe: opt.meta?.iframe,
          text: opt.meta?.text
        }))
      : meta?.slides || []
  const currentSlide = slides[step.slideIndex]
  const { kind, image, iframe, text } = currentSlide ?? {}
  const [startedAtMs, setStartedAtMs] = useState(() => Date.now())
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    const startedAt = Date.now()
    setStartedAtMs(startedAt)
    setNowMs(startedAt)
  }, [challengeId, step.slideIndex])

  useEffect(() => {
    if (minimumSeconds <= 0) return

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now())
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [minimumSeconds])

  const minimumAdvance = useMemo(() => {
    if (minimumSeconds <= 0) {
      return { isLocked: false, label: '' }
    }

    const elapsedSeconds = Math.floor((nowMs - startedAtMs) / 1000)
    const remainingSeconds = Math.max(0, minimumSeconds - elapsedSeconds)
    const minutes = Math.floor(remainingSeconds / 60)
    const seconds = remainingSeconds % 60

    return {
      isLocked: remainingSeconds > 0,
      label: `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  }, [minimumSeconds, nowMs, startedAtMs])

  const handleContinue = async () => {
    if (!challenge) return

    try {
      const { ok } = await onChallengeAnswer({
        uid: challenge.uid,
        answer: 'Visualizado',
        skipValidation: true
      })

      if (ok) enableContinue()
      verifyChallenge({ correct: true })
      goNext()
    } catch (error) {
      showErrorAlert(error)
    }
  }

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>
      <HUD.Body className="flex flex-col gap-4">
        <HUD.Description challenge={challenge} />

        <div className="flex flex-col animate-fade-in">
          {kind === 'image' && (
            <div className="aspect-video w-full mb-10">
              <img
                src={_.get(image, 'file.url')}
                alt=""
                className="w-full h-full object-cover object-center rounded-xl"
              />
            </div>
          )}
          {kind === 'iframe' && (
            <div
              className="aspect-video w-full mb-10 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:aspect-video [&>iframe]:block [&>iframe]:border-0"
              dangerouslySetInnerHTML={{ __html: iframe }}
            />
          )}
          <span
            className="text-gray-800 text-md md:text-xl leading-relaxed block"
            dangerouslySetInnerHTML={{ __html: text }}
          />
          <span className="w-full pb-20 block"></span>
        </div>
      </HUD.Body>

      <LevelFooter>
        {!step.isFirstSlide && (
          <Button type="button" icon onClick={goPrev}>
            <HugeiconsIcon icon={ArrowTurnBackwardIcon} size={22} />
          </Button>
        )}
        {step.isLastSlide ? (
          <Button
            type="button"
            onClick={handleContinue}
            disabled={minimumAdvance.isLocked || isSubmitting}
            className="btn btn-info flex-grow-1"
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="size-5 animate-spin" />
                <span>Carregando</span>
              </>
            ) : (
              <span>
                {minimumAdvance.isLocked
                  ? `Aguarde ${minimumAdvance.label}`
                  : 'Continuar'}
              </span>
            )}
          </Button>
        ) : (
          <Button
            type="button"
            variant="success"
            onClick={goNext}
            className="flex-grow-1"
            disabled={minimumAdvance.isLocked}
          >
            <span>
              {minimumAdvance.isLocked
                ? `Aguarde ${minimumAdvance.label}`
                : 'Próximo'}
            </span>
          </Button>
        )}
      </LevelFooter>
    </HUD.Card>
  )
}
